import { useState, useEffect, useCallback } from 'react';
import { db } from '@/config/firebase';
import { doc, getDoc, Timestamp } from 'firebase/firestore';

// Campaign status types in the specified order
const CAMPAIGN_STATUS_TYPES = [
  'Broad Status',
  'CA Auto Status', 
  'CTA Status',
  'Exact Status',
  'Full Auto Status',
  'Micro Auto Status',
  'PP Exact Status',
  'PP PTA Status',
  'PTA Status',
  'ROS Exact Status',
  'ROS PTA Status',
  'TOS Exact Status',
  'TOS PTA Status'
] as const;

export type CampaignStatusType = typeof CAMPAIGN_STATUS_TYPES[number];

export interface CampaignStatusEntry {
  id: string;
  statusType: CampaignStatusType;
  date: Timestamp;
  yourName: string;
  newValue: string;
  note: string;
  daysAgo: number;
}

export interface CampaignStatusData {
  'Broad Status': CampaignStatusEntry[];
  'CA Auto Status': CampaignStatusEntry[];
  'CTA Status': CampaignStatusEntry[];
  'Exact Status': CampaignStatusEntry[];
  'Full Auto Status': CampaignStatusEntry[];
  'Micro Auto Status': CampaignStatusEntry[];
  'PP Exact Status': CampaignStatusEntry[];
  'PP PTA Status': CampaignStatusEntry[];
  'PTA Status': CampaignStatusEntry[];
  'ROS Exact Status': CampaignStatusEntry[];
  'ROS PTA Status': CampaignStatusEntry[];
  'TOS Exact Status': CampaignStatusEntry[];
  'TOS PTA Status': CampaignStatusEntry[];
}

interface UseCampaignStatusHistoryResult {
  data: CampaignStatusData | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch campaign status history from pmt2_ledger collection
 * Organizes data by status type and calculates days ago for each entry
 */
export const useCampaignStatusHistory = (
  companyName?: string,
  asin?: string
): UseCampaignStatusHistoryResult => {
  const [data, setData] = useState<CampaignStatusData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const calculateDaysAgo = (timestamp: Timestamp): number => {
    const now = new Date();
    const entryDate = timestamp.toDate();
    const diffTime = Math.abs(now.getTime() - entryDate.getTime());
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  };

  // Convert Excel serial date to JavaScript Date
  const excelDateToJSDate = (excelDate: number): Date => {
    // Excel epoch is December 30, 1899 (Excel treats January 1, 1900 as day 1)
    // Excel serial date 1 = January 1, 1900
    // Excel serial date 45677 should = January 20, 2025
    const excelEpoch = new Date(1899, 11, 30); // December 30, 1899 (day 0 in Excel)
    const jsDate = new Date(excelEpoch.getTime() + excelDate * 24 * 60 * 60 * 1000);
    
    return jsDate;
  };

  // Smart date detection - determine if a number is likely an Excel serial date
  const isLikelyExcelSerialDate = (value: number): boolean => {
    // Excel serial dates for reasonable date ranges:
    // 1 = Jan 1, 1900
    // 36526 = Jan 1, 2000
    // 44927 = Jan 1, 2023
    // 45677 = Jan 20, 2025
    // 46751 = Jan 1, 2028
    
    // More conservative range - only recent dates that make sense for campaign data
    // Campaigns wouldn't have data from 1900-1990, so use a more recent range
    return value >= 40000 && value <= 50000; // Roughly 2009-2037
  };

  const fetchCampaignStatusHistory = useCallback(async () => {
    if (!companyName || !asin) {
      setData(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Initialize data structure with empty arrays for each status type
      const statusData: CampaignStatusData = {} as CampaignStatusData;
      CAMPAIGN_STATUS_TYPES.forEach(statusType => {
        statusData[statusType] = [];
      });

      // Fetch data for each campaign status type
      const fetchPromises = CAMPAIGN_STATUS_TYPES.map(async (statusType) => {
        try {
          const docRef = doc(db, 'pmt2_ledger', companyName, asin, statusType);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            const docData = docSnap.data();
            
            if (docData.entries && Array.isArray(docData.entries)) {
              const entries: CampaignStatusEntry[] = docData.entries
                .map((entry: any, index: number) => {
                  // Handle case-insensitive field mapping
                  const date = entry.Date || entry.date || Timestamp.now();
                  const yourName = entry['Your Name'] || entry.yourName || entry['your name'] || '';
                  const newValue = entry['New Value'] || entry.newValue || entry['new value'] || '';
                  const note = entry.Note || entry.note || entry['note'] || '';

                  // Ensure date is a Timestamp with proper validation
                  let timestamp: Timestamp;
                  try {
                    if (date instanceof Timestamp) {
                      timestamp = date;
                    } else if (typeof date === 'string') {
                      // Check if string is a numeric Excel serial date
                      const numericValue = parseFloat(date);
                      if (!isNaN(numericValue) && isLikelyExcelSerialDate(numericValue)) {
                        // String contains Excel serial date number
                        const jsDate = excelDateToJSDate(numericValue);
                        timestamp = Timestamp.fromDate(jsDate);
                      } else {
                        // Try to parse as regular date string (e.g., "9/30/24", "1/20/25")
                        const parsedDate = new Date(date);
                        if (isNaN(parsedDate.getTime())) {
                          console.warn(`Invalid date string for ${statusType}:`, date);
                          timestamp = Timestamp.now();
                        } else {
                          timestamp = Timestamp.fromDate(parsedDate);
                        }
                      }
                    } else if (typeof date === 'number') {
                      // Handle numeric dates - use smart detection for Excel serial dates
                      if (isLikelyExcelSerialDate(date)) {
                        // Likely Excel serial date
                        const jsDate = excelDateToJSDate(date);
                        timestamp = Timestamp.fromDate(jsDate);
                      } else if (date > 1000000000 && date < 10000000000) {
                        // Unix timestamp in seconds
                        timestamp = new Timestamp(Math.floor(date), 0);
                      } else if (date > 1000000000000) {
                        // Unix timestamp in milliseconds
                        timestamp = new Timestamp(Math.floor(date / 1000), 0);
                      } else {
                        // Unknown numeric format, log and use current time
                        console.warn(`Unknown numeric date format for ${statusType}:`, date);
                        timestamp = Timestamp.now();
                      }
                    } else {
                      timestamp = Timestamp.now();
                    }
                  } catch (timestampError) {
                    console.warn(`Error creating timestamp for ${statusType}:`, date, timestampError);
                    timestamp = Timestamp.now();
                  }

                  return {
                    id: `${statusType}_${index}_${timestamp.toMillis()}`,
                    statusType,
                    date: timestamp,
                    yourName,
                    newValue,
                    note,
                    daysAgo: calculateDaysAgo(timestamp)
                  };
                })
                .filter(entry => entry.date && entry.newValue) // Filter out invalid entries
                .sort((a, b) => b.date.toMillis() - a.date.toMillis()); // Sort newest first

              statusData[statusType] = entries;
            }
          }
        } catch (statusError) {
          console.error(`Error fetching ${statusType} data:`, statusError);
          // Continue with other status types even if one fails
        }
      });

      await Promise.all(fetchPromises);
      setData(statusData);
    } catch (err) {
      console.error('Error fetching campaign status history:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch campaign status history'));
    } finally {
      setIsLoading(false);
    }
  }, [companyName, asin]);

  const refetch = useCallback(() => {
    fetchCampaignStatusHistory();
  }, [fetchCampaignStatusHistory]);

  useEffect(() => {
    fetchCampaignStatusHistory();
  }, [fetchCampaignStatusHistory]);

  return {
    data,
    isLoading,
    error,
    refetch
  };
};

export default useCampaignStatusHistory;